import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  type DrawerProps,
  IconButton,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { X, Check, Sparkles, Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { nextPlanAbove, type Plan } from '../core/features'
import { useAnalytics } from '../hooks/useAnalytics'
import type { EntitlementInfo } from '../hooks/useSystemInfo'
import { usePlanContent } from '../hooks/usePlanContent'
import { compareVersions } from '../utils/announcements'
import { buildBuyUrl } from '../utils/externalLinks'
import { getPlanDrawerColors } from './planDrawerColors'
import PlanUpcomingFeatures from './PlanUpcomingFeatures'

interface PlanInfoDrawerProps {
  open: boolean
  onClose: () => void
  plan: Plan
  appVersion?: string
  initialSelectedPlan?: Plan
  features?: Record<string, Plan>
  entitlement?: EntitlementInfo
  container?: DrawerProps['container']
}

const UPGRADE_PLANS: Plan[] = ['pro', 'enterprise']
const REPLACED_PLAN_CONTENT_FEATURE_IDS: Record<string, string> = {
  multi_source_policies: 'backup_plan_mixed_sources',
  rclone_support: 'rclone',
}

type ActiveTab = 'your-plan' | 'upgrade'

function isFeatureAvailableInCurrentVersion(
  feature: { available_in?: string },
  appVersion?: string
) {
  return Boolean(
    feature.available_in && appVersion && compareVersions(appVersion, feature.available_in) >= 0
  )
}

function isVersionedUpcomingFeature(feature: { available_in?: string }, appVersion?: string) {
  return Boolean(feature.available_in) && !isFeatureAvailableInCurrentVersion(feature, appVersion)
}

function isComingSoonFeature(feature: { availability?: string }) {
  return feature.availability === 'coming_soon'
}

function isFeatureIncluded(
  feature: { availability?: string; available_in?: string },
  appVersion?: string
) {
  return (
    feature.availability === 'included' || isFeatureAvailableInCurrentVersion(feature, appVersion)
  )
}

function isReplacedByVisibleFeature(featureId: string, visibleFeatureIds: Set<string>) {
  const replacementFeatureId = REPLACED_PLAN_CONTENT_FEATURE_IDS[featureId]

  return Boolean(replacementFeatureId && visibleFeatureIds.has(replacementFeatureId))
}

function getDefaultSelectedPlan(plan: Plan, initialSelectedPlan?: Plan): Plan {
  if (initialSelectedPlan && UPGRADE_PLANS.includes(initialSelectedPlan)) {
    return initialSelectedPlan
  }

  return UPGRADE_PLANS.includes(plan) ? plan : UPGRADE_PLANS[0]
}

function getDefaultActiveTab(plan: Plan, isFeatureTrial = false): ActiveTab {
  // A running Pro preview is the reader's plan for now: open on it.
  return plan === 'community' && !isFeatureTrial ? 'upgrade' : 'your-plan'
}

function normalizePlan(plan: Plan | string | null | undefined): Plan {
  if (plan === 'pro' || plan === 'enterprise') {
    return plan
  }

  return 'community'
}

const PLAN_RANK: Record<Plan, number> = { community: 0, pro: 1, enterprise: 2 }

export default function PlanInfoDrawer({
  open,
  onClose,
  plan,
  appVersion,
  initialSelectedPlan,
  features,
  entitlement,
  container,
}: PlanInfoDrawerProps) {
  const { t } = useTranslation()
  const muiTheme = useTheme()
  const { trackPlan, EventAction } = useAnalytics()
  const { features: planContentFeatures } = usePlanContent()
  const normalizedPlan = normalizePlan(plan)
  const [selectedPlan, setSelectedPlan] = useState<Plan>(
    getDefaultSelectedPlan(normalizedPlan, initialSelectedPlan)
  )
  // A per-feature trial only exists on Community; a paid plan is never a preview.
  const isFeatureTrial =
    normalizedPlan === 'community' &&
    !(entitlement?.is_full_access && entitlement.status === 'active') &&
    entitlement?.status === 'active' &&
    (entitlement.trial_features ?? []).length > 0
  const [activeTab, setActiveTab] = useState<ActiveTab>(
    getDefaultActiveTab(normalizedPlan, isFeatureTrial)
  )
  const [referenceNowMs, setReferenceNowMs] = useState<number | null>(null)

  const fullAccessExpiry = entitlement?.expires_at
    ? new Date(entitlement.expires_at).toLocaleDateString()
    : null
  const isFullAccess = entitlement?.is_full_access && entitlement.status === 'active'
  const isLite = normalizedPlan === 'pro' && entitlement?.license_plan === 'lite'
  // A per-feature trial runs on the plan the install already has; the drawer
  // names the preview and lists what it opened (spec 2026-09-21, section 3).
  const trialFeatures = isFeatureTrial ? (entitlement?.trial_features ?? []) : []
  // Every manifest row the trial's gate keys unlock, or the bare key when the
  // manifest has no row for it.
  const trialFeatureItems = trialFeatures.flatMap<{
    id: string
    label: string
    description?: string
  }>(({ feature }) => {
    const rows = planContentFeatures.filter((f) => f.id === feature || f.gate === feature)
    return rows.length > 0
      ? rows.map((f) => ({ id: f.id, label: f.label, description: f.description }))
      : [{ id: feature, label: feature, description: undefined }]
  })
  const planLabel = (value: Plan) => t(`plan.labels.${value}`)

  const daysRemaining =
    entitlement?.expires_at && referenceNowMs !== null
      ? Math.max(
          0,
          Math.ceil(
            (new Date(entitlement.expires_at).getTime() - referenceNowMs) / (1000 * 60 * 60 * 24)
          )
        )
      : null

  const drawerColors = getPlanDrawerColors(muiTheme)
  const currentPlanColors =
    drawerColors.plans[isFullAccess ? 'enterprise' : isFeatureTrial ? 'pro' : normalizedPlan]
  const selectedPlanColors = drawerColors.plans[selectedPlan]
  const communityPlanColors = drawerColors.plans.community
  const fullAccessPlanColors = drawerColors.plans.enterprise
  const countdownColors = isFullAccess ? fullAccessPlanColors : drawerColors.plans.pro
  const color = currentPlanColors.accent
  const label = isFullAccess
    ? t('plan.fullAccessLabel')
    : isFeatureTrial
      ? t('plan.featureTrialLabel')
      : isLite
        ? t('plan.liteLabel')
        : planLabel(normalizedPlan)

  const selectedColor = selectedPlanColors.accent
  const visibleFeatureIds = Object.entries(features ?? {}).filter(
    ([, required]) => required === selectedPlan
  )
  const visibleFeatureIdSet = new Set(visibleFeatureIds.map(([key]) => key))
  const manifestFeaturesForPlan = planContentFeatures.filter(
    (feature) => feature.plan === selectedPlan
  )
  const visibleFeatures = manifestFeaturesForPlan
    .filter(
      (feature) =>
        (visibleFeatureIdSet.has(feature.id) || isFeatureIncluded(feature, appVersion)) &&
        !isReplacedByVisibleFeature(feature.id, visibleFeatureIdSet)
    )
    .map((feature) => ({
      id: feature.id,
      label: feature.label,
      description: feature.description,
    }))

  const backendOnlyVisibleFeatures = visibleFeatureIds
    .filter(([key]) => !manifestFeaturesForPlan.some((feature) => feature.id === key))
    .map(([key]) => {
      const content = planContentFeatures.find((feature) => feature.id === key)
      return {
        id: key,
        label: content?.label ?? key,
        description: content?.description ?? '',
      }
    })

  const currentFeatures =
    visibleFeatures.length > 0 || backendOnlyVisibleFeatures.length > 0
      ? [...visibleFeatures, ...backendOnlyVisibleFeatures]
      : manifestFeaturesForPlan
          .filter((feature) => isFeatureIncluded(feature, appVersion))
          .map((feature) => ({
            id: feature.id,
            label: feature.label,
            description: feature.description,
          }))

  const upcomingVersionedFeatures = manifestFeaturesForPlan.filter(
    (feature) =>
      isVersionedUpcomingFeature(feature, appVersion) &&
      !visibleFeatureIdSet.has(feature.id) &&
      !isReplacedByVisibleFeature(feature.id, visibleFeatureIdSet)
  )

  const comingSoonFeatures = manifestFeaturesForPlan.filter(
    (feature) =>
      !isVersionedUpcomingFeature(feature, appVersion) &&
      !visibleFeatureIdSet.has(feature.id) &&
      !isReplacedByVisibleFeature(feature.id, visibleFeatureIdSet) &&
      (isComingSoonFeature(feature) || feature.availability === undefined)
  )

  const communityFeatures = planContentFeatures.filter(
    (f) => f.plan === 'community' && isFeatureIncluded(f, appVersion)
  )
  // Community features a newer release brings: an older install learns what
  // an update gives it, the same way the Upgrade tab does for Pro.
  const upcomingCommunityFeatures = planContentFeatures.filter(
    (f) => f.plan === 'community' && isVersionedUpcomingFeature(f, appVersion)
  )

  useEffect(() => {
    if (open) {
      setSelectedPlan(getDefaultSelectedPlan(normalizedPlan, initialSelectedPlan))
      setActiveTab(getDefaultActiveTab(normalizedPlan, isFeatureTrial))
      setReferenceNowMs(Date.now())
    }
  }, [initialSelectedPlan, isFeatureTrial, normalizedPlan, open])

  // The footer sells the tier above the current plan, or the plan being browsed on
  // the Upgrade tab when that is higher than what the instance already has. On
  // Enterprise there is nothing to sell, so the footer button is hidden. The same
  // value drives the href, the label, and the analytics event.
  const browsedUpgrade =
    activeTab === 'upgrade' && PLAN_RANK[selectedPlan] > PLAN_RANK[normalizedPlan]
      ? (selectedPlan as 'pro' | 'enterprise')
      : null
  const buyPlan: 'pro' | 'enterprise' | null = browsedUpgrade ?? nextPlanAbove(normalizedPlan)
  const buySource = isFullAccess
    ? 'app-trial'
    : entitlement?.ui_state === 'full_access_expired'
      ? 'app-expired'
      : 'app-drawer'
  const buyOffer = entitlement?.ui_state === 'full_access_expired' ? 'expired' : undefined

  const handleBuyClick = () => {
    if (!buyPlan) return
    trackPlan(EventAction.VIEW, {
      surface: 'plan_drawer',
      operation: 'open_buy_link',
      selected_plan: buyPlan,
    })
  }

  // Shown at the top of both tabs once full access has ended. The CTA sits under
  // the text rather than in the Alert action slot, which squeezed the message to
  // one word per line inside the narrow drawer.
  const expiredNotice = (
    <Alert
      severity="warning"
      sx={{ mb: 2, fontSize: '0.75rem', '& .MuiAlert-message': { width: '100%' } }}
    >
      {t('plan.fullAccessExpiredNotice')}
      <Button
        color="inherit"
        variant="outlined"
        size="small"
        fullWidth
        component="a"
        href={buildBuyUrl({ plan: 'pro', src: 'app-expired', offer: 'expired' })}
        target="_blank"
        rel="noreferrer"
        onClick={() =>
          trackPlan(EventAction.VIEW, {
            surface: 'plan_drawer',
            operation: 'open_buy_link',
            selected_plan: 'pro',
            context: 'full_access_expired',
          })
        }
        sx={{ mt: 1.25, fontWeight: 700 }}
      >
        {t('plan.expiredUpgradeCta')}
      </Button>
    </Alert>
  )

  const yourPlanTabColor = color
  const upgradeTabColor = selectedColor

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      container={container}
      sx={{
        '& .MuiDrawer-paper': {
          width: 340,
          boxSizing: 'border-box',
          bgcolor: drawerColors.paper,
        },
      }}
      slotProps={{
        transition: {
          onExited: () => {
            setSelectedPlan(getDefaultSelectedPlan(normalizedPlan, initialSelectedPlan))
            setActiveTab(getDefaultActiveTab(normalizedPlan, isFeatureTrial))
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  bgcolor: currentPlanColors.iconSurface,
                  border: '1px solid',
                  borderColor: currentPlanColors.iconBorder,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Sparkles size={16} style={{ color }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: drawerColors.sectionText,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {t('plan.currentPlan')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color, lineHeight: 1, fontSize: '1.1rem' }}
                >
                  {label}
                </Typography>
                {isLite && (
                  <Typography
                    sx={{ fontSize: '0.7rem', color: drawerColors.secondaryText, mt: 0.5 }}
                  >
                    {t('plan.liteDescription')}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
              <X size={16} />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Tab bar */}
        <Box
          sx={{
            display: 'flex',
            px: 2.5,
            pt: 1.25,
            pb: 0,
            gap: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {(['your-plan', 'upgrade'] as ActiveTab[]).map((tab) => {
            const tabColor = tab === 'your-plan' ? yourPlanTabColor : upgradeTabColor
            const isActive = activeTab === tab
            const tabLabel = tab === 'your-plan' ? t('plan.yourPlanTab') : t('plan.upgradeTab')
            return (
              <Box
                key={tab}
                onClick={() => setActiveTab(tab)}
                sx={{
                  px: 1.5,
                  pb: 1,
                  cursor: 'pointer',
                  borderBottom: '2px solid',
                  borderColor: isActive ? tabColor : 'transparent',
                  mr: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? tabColor : drawerColors.secondaryText,
                    letterSpacing: '0.01em',
                  }}
                >
                  {tabLabel}
                </Typography>
              </Box>
            )
          })}
        </Box>

        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
          {activeTab === 'your-plan' && (
            <>
              {entitlement?.ui_state === 'full_access_expired' && expiredNotice}
              {entitlement?.last_refresh_error && (
                <Alert severity="warning" sx={{ mb: 2, fontSize: '0.75rem' }}>
                  {t('plan.lastRefreshError', { error: entitlement.last_refresh_error })}
                </Alert>
              )}

              {/* Countdown banner: full access or a Pro preview */}
              {(isFullAccess || isFeatureTrial) && fullAccessExpiry && daysRemaining !== null && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: '8px',
                    bgcolor: countdownColors.surface,
                    border: '1px solid',
                    borderColor: countdownColors.border,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: countdownColors.accent,
                      lineHeight: 1.3,
                      mb: 0.5,
                    }}
                  >
                    {isFullAccess
                      ? t('plan.fullAccessCountdown', { count: daysRemaining })
                      : t('plan.featureTrialCountdown', { count: daysRemaining })}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: countdownColors.description,
                      lineHeight: 1.5,
                    }}
                  >
                    {isFullAccess
                      ? t('plan.fullAccessEndsNotice', { date: fullAccessExpiry })
                      : t('plan.featureTrialEndsNotice', { date: fullAccessExpiry })}
                  </Typography>
                  {isFeatureTrial && (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: countdownColors.accent,
                          mb: 0.5,
                        }}
                      >
                        {t('plan.featureTrialOpens')}
                      </Typography>
                      {trialFeatureItems.map(({ id, label: name, description }) => (
                        <Box
                          key={id}
                          sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.25 }}
                        >
                          <Check
                            size={12}
                            style={{ color: countdownColors.accent, flexShrink: 0, marginTop: 2 }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                color: countdownColors.accent,
                                lineHeight: 1.3,
                              }}
                            >
                              {name}
                            </Typography>
                            {description && (
                              <Typography
                                sx={{
                                  fontSize: '0.68rem',
                                  color: countdownColors.description,
                                  lineHeight: 1.45,
                                }}
                              >
                                {description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Community features section label */}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.6rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: drawerColors.sectionText,
                  display: 'block',
                  mb: 1.25,
                }}
              >
                {t('plan.communityIncluded')}
              </Typography>

              {/* Community feature list */}
              {communityFeatures.map((feature) => (
                <Box key={feature.id} sx={{ display: 'flex', gap: 1.25, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '4px',
                      bgcolor: communityPlanColors.iconSurface,
                      border: '1px solid',
                      borderColor: communityPlanColors.iconBorder,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.125,
                    }}
                  >
                    <Check
                      size={10}
                      style={{ color: communityPlanColors.accent }}
                      strokeWidth={3}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.3,
                      }}
                    >
                      {feature.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: communityPlanColors.description,
                        lineHeight: 1.4,
                        mt: 0.25,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {upcomingCommunityFeatures.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <PlanUpcomingFeatures
                    title={t('plan.plannedReleases', { plan: planLabel('community') })}
                    features={upcomingCommunityFeatures}
                    colors={communityPlanColors}
                    sectionColor={drawerColors.sectionText}
                  />
                </>
              )}

              {/* Upgrade nudge box */}
            </>
          )}

          {activeTab === 'upgrade' && (
            <>
              {entitlement?.ui_state === 'full_access_expired' && expiredNotice}
              {entitlement?.last_refresh_error && (
                <Alert severity="warning" sx={{ mb: 2, fontSize: '0.75rem' }}>
                  {t('plan.lastRefreshError', { error: entitlement.last_refresh_error })}
                </Alert>
              )}

              {/* Plan selector */}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.6rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: drawerColors.sectionText,
                  display: 'block',
                  mb: 1.25,
                }}
              >
                {t('plan.plans')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 0.75,
                  mb: 2.5,
                }}
              >
                {UPGRADE_PLANS.map((p) => {
                  const planColors = drawerColors.plans[p]
                  const isSelected = p === selectedPlan

                  return (
                    <Box
                      key={p}
                      onClick={() => {
                        setSelectedPlan(p)
                        trackPlan(EventAction.VIEW, {
                          surface: 'plan_drawer',
                          operation: 'select_plan',
                          selected_plan: p,
                        })
                      }}
                      sx={{
                        p: 1,
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: isSelected ? planColors.border : 'divider',
                        bgcolor: isSelected ? planColors.surface : 'transparent',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: planColors.border,
                          bgcolor: planColors.hoverSurface,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          color: isSelected ? planColors.accent : drawerColors.secondaryText,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {planLabel(p)}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Feature list for selected plan */}
              {currentFeatures.length > 0 && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.6rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: drawerColors.sectionText,
                      }}
                    >
                      {t('plan.planFeatures', { plan: planLabel(selectedPlan) })}
                    </Typography>
                  </Box>
                  {currentFeatures.map((feature) => (
                    <Box key={feature.id} sx={{ display: 'flex', gap: 1.25, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '4px',
                          bgcolor: selectedPlanColors.iconSurface,
                          border: '1px solid',
                          borderColor: selectedPlanColors.iconBorder,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: 0.125,
                        }}
                      >
                        <Check size={10} style={{ color: selectedColor }} strokeWidth={3} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            color: 'text.primary',
                            lineHeight: 1.3,
                          }}
                        >
                          {feature.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: selectedPlanColors.description,
                            lineHeight: 1.4,
                            mt: 0.25,
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              {upcomingVersionedFeatures.length > 0 && (
                <>
                  {currentFeatures.length > 0 && <Divider sx={{ my: 2 }} />}
                  <PlanUpcomingFeatures
                    title={t('plan.plannedReleases', { plan: planLabel(selectedPlan) })}
                    features={upcomingVersionedFeatures}
                    colors={selectedPlanColors}
                    sectionColor={drawerColors.sectionText}
                  />
                </>
              )}

              {comingSoonFeatures.length > 0 && (
                <>
                  {(currentFeatures.length > 0 || upcomingVersionedFeatures.length > 0) && (
                    <Divider sx={{ my: 2 }} />
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.6rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: drawerColors.sectionText,
                      }}
                    >
                      {t('plan.upcomingFeatures', { plan: planLabel(selectedPlan) })}
                    </Typography>
                    <Chip
                      icon={<Clock size={10} />}
                      label={t('plan.comingSoon')}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        bgcolor: selectedPlanColors.statusSurface,
                        color: selectedColor,
                        border: '1px solid',
                        borderColor: selectedPlanColors.statusBorder,
                        '& .MuiChip-icon': { color: selectedColor, ml: 0.5 },
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </Box>
                  {comingSoonFeatures.map((feature) => (
                    <Box key={feature.id} sx={{ display: 'flex', gap: 1.25, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '4px',
                          bgcolor: selectedPlanColors.iconSurface,
                          border: '1px dashed',
                          borderColor: selectedPlanColors.iconBorder,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: 0.125,
                        }}
                      >
                        <Clock size={9} style={{ color: selectedColor }} strokeWidth={2.5} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            color: 'text.primary',
                            lineHeight: 1.3,
                          }}
                        >
                          {feature.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: selectedPlanColors.description,
                            lineHeight: 1.4,
                            mt: 0.25,
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  {isFullAccess && (
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: selectedColor,
                        lineHeight: 1.4,
                        mt: 0.25,
                        fontWeight: 600,
                      }}
                    >
                      {t('plan.featureUnavailableInCommunity')}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </Box>

        <Divider />

        {/* Footer — consistent across both tabs */}
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {isFullAccess && fullAccessExpiry && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Clock size={13} style={{ color, flexShrink: 0, marginTop: 2 }} />
              <Typography
                sx={{ fontSize: '0.7rem', color: drawerColors.secondaryText, lineHeight: 1.5 }}
              >
                {t('plan.fullAccessActiveNotice', { date: fullAccessExpiry })}
              </Typography>
            </Box>
          )}
          {buyPlan && (
            <Button
              component="a"
              href={buildBuyUrl({ plan: buyPlan, src: buySource, offer: buyOffer })}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              fullWidth
              onClick={handleBuyClick}
            >
              {t('plan.buyLink', { plan: planLabel(buyPlan) })}
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
